import { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../../../utils/axiosInstance";
import { AuthContext } from "../../../../context/AuthContext";
import FormInput from "../../../../components/Form/FormInput";
import FormSelect from "../../../../components/Form/FormSelect";
import CheckboxField from "../../../../components/Form/CheckboxField";
import { getTimezonesWithOffset } from "../../../../utils/constants/timezones";

const staticTimezones = getTimezonesWithOffset();

const CreateManagerForm = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [form, setForm] = useState({
    role_id: "",
    client_pool_id: "",
    first_name: "",
    last_name: "",
    gender: "",
    email: "",
    phone: "",
    teams_id: "",
    zoom_id: "",
    timezone: "",
    can_login: true,
  });

  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [success, setSuccess] = useState("");
  const [clientPools, setClientPools] = useState([]);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    const fetchClientPools = async () => {
      try {
        const res = await api.get("/client-pools", { withCredentials: true });
        const pools = res.data.data.filter((pool) => pool.admin_id === user.id);
        setClientPools(pools);
      } catch (err) {
        console.error("Failed to fetch client pools:", err.response || err);
      }
    };

    fetchClientPools();
  }, [user.id]);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res = await api.get("/manager-roles", { withCredentials: true });
        setRoles(res.data.data);
      } catch (err) {
        console.error("Failed to fetch roles:", err.response || err);
      }
    };

    fetchRoles();
  }, []);

  const validate = () => {
    const currentErrors = {};
    if (!form.role_id) currentErrors.role_id = "Role is required.";
    if (!form.first_name) currentErrors.first_name = "First name is required.";
    if (!form.last_name) currentErrors.last_name = "Last name is required.";
    if (!form.email) currentErrors.email = "Email is required.";
    setErrors(currentErrors);
    return Object.keys(currentErrors).length === 0;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let errorMsg = "";
    if (
      ["role_id", "first_name", "last_name", "email"].includes(name) &&
      !value
    ) {
      errorMsg =
        name === "role_id"
          ? "Role is required."
          : name === "email"
          ? "Email is required."
          : `${name.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase())} is required.`;
    }
    setErrors((prev) => ({ ...prev, [name]: errorMsg }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors({ ...errors, [name]: "" });
    setServerError("");
    setSuccess("");
  };

  const formatFormData = () => {
    const f = { ...form };

    if (f.client_pool_id === "") f.client_pool_id = null;
    else f.client_pool_id = Number(f.client_pool_id);

    if (f.role_id !== "") f.role_id = Number(f.role_id);

    return f;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const payload = formatFormData();
      const response = await api.post("/managers", payload, {
        withCredentials: true,
      });
      setSuccess(response.data.message);
      setServerError("");
      navigate("/managers");
    } catch (err) {
      console.error(err.response?.data || err);
      setServerError(
        err.response?.data?.message ||
          "Something went wrong while creating manager."
      );
      setSuccess("");
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className="bg-white shadow-sm border border-gray-100 rounded-lg p-5 space-y-6"
    >
      <section className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Manager Details
          </h3>
          <button
            type="button"
            onClick={() => navigate("/system-roles")}
            className="text-blue-600 hover:text-blue-800 underline text-xs"
          >
            System roles and access permissions
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4 items-start">
          <FormSelect
            label="Role"
            name="role_id"
            value={form.role_id}
            onChange={handleChange}
            onBlur={handleBlur}
            options={roles.map((r) => ({ value: r.id, label: r.name }))}
            error={errors.role_id}
            required
          />

          <FormSelect
            label="Client Pool"
            name="client_pool_id"
            value={form.client_pool_id}
            onChange={handleChange}
            onBlur={handleBlur}
            options={clientPools.map((pool) => ({
              value: pool.id,
              label: pool.name,
            }))}
            error={errors.client_pool_id}
          />

          <FormInput
            label="First Name"
            name="first_name"
            value={form.first_name}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.first_name}
            required
          />

          <FormInput
            label="Last Name"
            name="last_name"
            value={form.last_name}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_name}
            required
          />

          <FormSelect
            label="Gender"
            name="gender"
            value={form.gender}
            onChange={handleChange}
            options={["Male", "Female", "Other"]}
            error={errors.gender}
          />

          <FormInput
            label="Email"
            name="email"
            value={form.email}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.email}
            type="email"
            required
          />

          <FormInput
            label="Phone"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            error={errors.phone}
          />

          <FormInput
            label="Teams ID"
            name="teams_id"
            value={form.teams_id}
            onChange={handleChange}
            error={errors.teams_id}
          />

          <FormInput
            label="Zoom ID"
            name="zoom_id"
            value={form.zoom_id}
            onChange={handleChange}
            error={errors.zoom_id}
          />

          <FormSelect
            label="Timezone"
            name="timezone"
            value={form.timezone}
            onChange={handleChange}
            options={staticTimezones}
            error={errors.timezone}
          />
        </div>

        <CheckboxField
          label="Can Login"
          name="can_login"
          checked={form.can_login}
          onChange={handleChange}
        />
      </section>

      {serverError && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded">
          {serverError}
        </div>
      )}
      {success && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm px-3 py-2 rounded">
          {success}
        </div>
      )}

      <div className="pt-3 border-t border-gray-100 space-y-3">
        <p className="text-xs text-gray-500">
          Fields marked with <span className="text-red-500">*</span> are mandatory.
        </p>
        <div className="flex gap-2 text-sm">
          <button
            type="submit"
            className="px-3 py-1.5 font-medium text-white bg-blue-600 hover:bg-blue-700 rounded"
          >
            Submit
          </button>
          <button
            type="button"
            onClick={() => navigate("/managers")}
            className="px-3 py-1.5 font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded"
          >
            Cancel
          </button>
        </div>
      </div>
    </form>
  );
};

export default CreateManagerForm;
